import {
  useTenantProfile,
  type TenantEntitlements,
} from '@/modules/settings/tenant-profile/hooks/use-tenant-profile';

/** Backend sentinel for an unlimited numeric limit. */
export const UNLIMITED = 9999;

export function isUnlimited(limit: number): boolean {
  return limit >= UNLIMITED;
}

const DEFAULTS: TenantEntitlements = {
  operators: UNLIMITED,
  flow_types: UNLIMITED,
  work_points: UNLIMITED,
  mobile_seats: UNLIMITED,
  documents_per_month: UNLIMITED,
  ai_monthly_token_budget: UNLIMITED,
  scale_count: 0,
  flavor: 'simple',
  modules: {
    treatment: false,
    pickup: false,
    nexus: false,
    accounting: false,
    market_prices: false,
    crm: false,
    signature_pad: false,
    id_scanner: false,
    analytics: false,
    smart_scales: false,
    partner_portal: false,
    ai_assistant: false,
    hr: false,
    siatd: false,
  },
};

/**
 * Resolved, admin-controlled entitlements for the current tenant (limits,
 * flow flavor, paid add-on modules). Falls back to permissive defaults while
 * the profile is loading so the UI never flickers a locked state on boot.
 */
export function useEntitlements(): TenantEntitlements {
  const profile = useTenantProfile();
  return profile.data?.data?.entitlements ?? DEFAULTS;
}

export type AddonModule = keyof TenantEntitlements['modules'];

/** Whether a paid add-on module is enabled for this tenant. */
export function useHasModule(module: AddonModule): boolean {
  return useEntitlements().modules[module];
}

/**
 * Whether the tenant profile (and therefore entitlements) is still being
 * fetched for the first time. `useEntitlements()` falls back to permissive
 * defaults while loading, which is fine for passively hiding nav items but
 * NOT safe for an active redirect — callers that redirect on a missing
 * module (e.g. `RequireEntitlement`) must wait for this to be `false` first.
 */
export function useEntitlementsLoading(): boolean {
  return useTenantProfile().isPending;
}
